// The billing period of a quote line, and the words for it.
//
// ⛔ ONE RULE, BOTH SIDES. The backend's quoteRenderer.js carries a PORT of
// periodOf / periodLabel / periodHeading (a Node service cannot import this
// module), and tests/quoteRenderer.test.js over there pins that port against a
// verbatim copy of this file's rule — exactly as quantityOf is pinned. Change
// the rule here and the backend test fails, which is the point: a builder that
// calls a line monthly while the document prints it once-off is the same
// silent disagreement the quantity fix closed.
//
// ── WHY A PERIOD, NOT A BOOLEAN ──────────────────────────────────────────────
// Craig's brief: "show in the quote to customer that the price is per month.
// Later we can look to add different periods." So nothing asks "is it
// recurring?" — everything asks "what is its period?", and today the answer
// is "month" for a line whose Type is Recurring and null for anything else.
// A quarterly or annual charge later is a new entry in PERIOD_WORDS, a way for
// the builder to set it on the line, and a stream per period in the totals —
// not a rewrite of every caller.
//
// ⚠ DELIBERATELY NOT READ: a `li.period` field. No builder writes one, and a
// field that changes the label but not the arithmetic is worse than none.
//
// ⚠ THE CUSTOMER READS WORDS. periodLabel is "per month" — it prints through
// Prince onto a document a client may sign. periodShort ("/mo") is for the
// builder's own panels and nowhere else.

export const MONTHLY = "month";

const PERIOD_WORDS = {
  month: { label: "per month", heading: "Monthly", short: "/mo" },
};

/** "month" for a Recurring line, null for a once-off one. */
export function periodOf(li) {
  return li && li.type === "recurring" ? MONTHLY : null;
}

/** The words on the customer document: "per month". */
export function periodLabel(period) {
  return (PERIOD_WORDS[period] || {}).label || `per ${period}`;
}

/** The heading over a stream of totals: "Monthly". */
export function periodHeading(period) {
  return (PERIOD_WORDS[period] || {}).heading || `Per ${period}`;
}

/** The builder's shorthand: "/mo". Never on the customer document. */
export function periodShort(period) {
  return (PERIOD_WORDS[period] || {}).short || `/${period}`;
}

/**
 * One figure for the builder's section / option / common badges, from a
 * totals object carrying `subtotal` plus its `onceOff` and `recurring` streams
 * (each `{ subtotal, count }`).
 *
 *   only once-off lines  -> "$499.00"          (what the badge always said)
 *   only recurring lines -> "$500.00/mo"
 *   both                 -> "$499.00 + $500.00/mo"
 *
 * ⚠ The mixed case is a SUM OF TWO DIFFERENT THINGS and must never collapse
 * to one number: $499 + $500/mo is not $999 of anything.
 */
export function streamsText(t, fmt = (n) => `$${(Number(n) || 0).toFixed(2)}`) {
  const rec = t && t.recurring;
  const once = t && t.onceOff;
  if (!rec || !rec.count) return fmt(t ? t.subtotal : 0);
  const monthly = `${fmt(rec.subtotal)}${periodShort(MONTHLY)}`;
  if (!once || !once.count) return monthly;
  return `${fmt(once.subtotal)} + ${monthly}`;
}
